<script setup lang="ts">
  import { watch } from 'vue';
  import { useFieldValue, useSetFieldValue } from 'vee-validate';
  import {
    applyNumberMask,
    formatPtBrDecimal,
    parsePtBrDecimal,
    scaleCanonical,
    type DecimalTypingOptions,
    type NumberMaskOptions,
  } from '@record-fill';
  import { useNumberInput } from '../../../model/useNumberInput';
  import { FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@/shared/ui';

  const props = defineProps<{
    name: string;
    placeholder: string;
    typing: DecimalTypingOptions;
    label?: string;
    inputClass?: string;
    maxlength?: number;
    mask?: NumberMaskOptions;
  }>();

  const { handleInput, handleKeydown, handlePaste, handleBlur } = useNumberInput(() => ({
    ...props.typing,
    mask: props.mask,
  }));

  const fieldValue = useFieldValue<string | undefined>(() => props.name);
  const setFieldValue = useSetFieldValue<string>(() => props.name);

  // The places change while the field keeps its text, so "1,20" is rescaled to "1,200" or "1,2"
  // as a number, never re-read as the digits "120".
  watch(
    () => props.mask?.scale,
    (scale) => {
      const mask = props.mask;
      if (!mask || scale === null || scale === undefined) return;
      const canonical = parsePtBrDecimal(fieldValue.value ?? '');
      if (canonical === null) return;
      const next = applyNumberMask(formatPtBrDecimal(scaleCanonical(canonical, scale)), mask);
      if (next !== fieldValue.value) setFieldValue(next);
    }
  );
</script>

<template>
  <FormField
    v-slot="{ componentField, handleChange }"
    :name="name"
  >
    <FormItem class="flex flex-col gap-2">
      <FormLabel
        v-if="label"
        class="text-sm text-gray-11 font-normal"
      >
        {{ label }}
      </FormLabel>
      <FormControl>
        <Input
          :model-value="componentField.modelValue"
          :name="componentField.name"
          type="text"
          inputmode="decimal"
          :placeholder="placeholder"
          :class="inputClass ?? 'h-11'"
          :maxlength="maxlength"
          @blur="handleBlur($event, handleChange); componentField.onBlur($event)"
          @keydown="handleKeydown($event, handleChange)"
          @paste="handlePaste($event, handleChange)"
          @input="handleInput($event, handleChange)"
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>
</template>
